/**
 * Score Cache Service
 *
 * Manages caching of user-specific BidWin scores in the database
 * to enable efficient DB-level sorting instead of client-side sorting.
 */

import { supabase } from './supabase';
import { ContractMatcher } from './matching/ContractMatcher';
import { Contract } from '../types';
import { Profile } from '../contexts/AuthContext';

interface ScoreCacheEntry {
  user_id: string;
  contract_id: string;
  bidwin_score: number;
  days_until_due: number | null;
  score_factors: {
    naicsMatch: number;
    setAsideMatch: number;
    capabilities: number;
    pricingDataAvailability: number;
    agencyHistory: number;
  };
}

/**
 * Calculate days until a contract is due
 */
function calculateDaysUntilDue(dueDate: string | null | undefined): number | null {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  const now = new Date();
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Calculate and cache BidWin scores for a batch of contracts
 */
export async function cacheContractScores(
  userId: string,
  contracts: Contract[],
  profile: Profile
): Promise<void> {
  if (!contracts.length || !profile) return;

  const entries: ScoreCacheEntry[] = contracts.map(contract => {
    // Calculate the BidWin score using ContractMatcher
    const score = ContractMatcher.calculateMatchScore(contract, profile);

    // Get detailed breakdown for debugging/transparency
    const naicsScore = ContractMatcher.calculateNaicsMatch(
      contract.naicsCode,
      profile.naics_codes || []
    );
    const setAsideScore = ContractMatcher.calculateSetAsideMatch(
      contract.setAsides || [],
      profile.qualified_set_asides || []
    );
    const capabilitiesScore = ContractMatcher.calculateCapabilitiesMatch(contract, profile);
    const pricingScore = ContractMatcher.calculatePricingDataAvailability(
      contract.naicsCode,
      profile.pricingData
    );
    const agencyScore = ContractMatcher.calculateAgencyMatch(
      contract.agency,
      profile.pastPerformance || []
    );

    return {
      user_id: userId,
      contract_id: contract.id || contract.notice_id!,
      bidwin_score: score,
      days_until_due: calculateDaysUntilDue(contract.dueDate),
      score_factors: {
        naicsMatch: Math.round(naicsScore * 100),
        setAsideMatch: Math.round(setAsideScore * 100),
        capabilities: Math.round(capabilitiesScore * 100),
        pricingDataAvailability: Math.round(pricingScore * 100),
        agencyHistory: Math.round(agencyScore * 100)
      }
    };
  });

  // Upsert scores in batches to avoid overwhelming the database
  const BATCH_SIZE = 100;
  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const batch = entries.slice(i, i + BATCH_SIZE);

    const { error } = await supabase
      .from('user_contract_scores')
      .upsert(
        batch.map(entry => ({
          user_id: entry.user_id,
          contract_id: entry.contract_id,
          bidwin_score: entry.bidwin_score,
          days_until_due: entry.days_until_due,
          score_factors: entry.score_factors,
          updated_at: new Date().toISOString()
        })),
        {
          onConflict: 'user_id,contract_id',
          ignoreDuplicates: false
        }
      );

    if (error) {
      console.error('Error caching contract scores:', error);
      // Don't throw - we can still function without caching
    }
  }
}

/**
 * Invalidate all cached scores for a user (call when profile changes)
 */
export async function invalidateUserScores(userId: string): Promise<void> {
  const { error } = await supabase
    .from('user_contract_scores')
    .delete()
    .eq('user_id', userId);

  if (error) {
    console.error('Error invalidating user scores:', error);
  }
}

/**
 * Get cached scores for contracts
 * Returns a map of contract_id -> { bidwin_score, days_until_due }
 */
export async function getCachedScores(
  userId: string,
  contractIds: string[]
): Promise<Map<string, { bidwin_score: number; days_until_due: number | null }>> {
  const scoreMap = new Map<string, { bidwin_score: number; days_until_due: number | null }>();

  if (!contractIds.length) return scoreMap;

  const { data, error } = await supabase
    .from('user_contract_scores')
    .select('contract_id, bidwin_score, days_until_due')
    .eq('user_id', userId)
    .in('contract_id', contractIds);

  if (error) {
    console.error('Error fetching cached scores:', error);
    return scoreMap;
  }

  for (const row of data || []) {
    scoreMap.set(row.contract_id, {
      bidwin_score: row.bidwin_score,
      days_until_due: row.days_until_due
    });
  }

  return scoreMap;
}

/**
 * Check if user has any cached scores
 */
export async function hasAnyScores(userId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('user_contract_scores')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .limit(1);

  if (error) {
    console.error('Error checking for cached scores:', error);
    return false;
  }

  return (count || 0) > 0;
}

/**
 * Get score cache stats for a user
 */
export async function getScoreCacheStats(userId: string): Promise<{
  totalCached: number;
  lastUpdated: string | null;
}> {
  const { data, error } = await supabase
    .from('user_contract_scores')
    .select('updated_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Error getting cache stats:', error);
    return { totalCached: 0, lastUpdated: null };
  }

  const { count } = await supabase
    .from('user_contract_scores')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  return {
    totalCached: count || 0,
    lastUpdated: data?.[0]?.updated_at || null
  };
}

/**
 * Batch calculate and cache scores for ALL contracts for a user.
 * This should be called when:
 * 1. User first logs in and has no cached scores
 * 2. User's profile changes significantly
 * 3. User manually requests a refresh
 *
 * This function runs in chunks to avoid timeout and memory issues.
 */
export async function batchCalculateAllScores(
  userId: string,
  profile: Profile,
  onProgress?: (completed: number, total: number) => void
): Promise<{ success: boolean; processed: number; errors: number }> {
  const CHUNK_SIZE = 50; // Process 50 contracts at a time
  let processed = 0;
  let errors = 0;

  try {
    // Get total count of contracts
    const { count: totalContracts, error: countError } = await supabase
      .from('sam_contracts')
      .select('*', { count: 'exact', head: true });

    if (countError) throw countError;
    const total = totalContracts || 0;

    if (total === 0) {
      return { success: true, processed: 0, errors: 0 };
    }

    // Process in chunks
    let offset = 0;
    while (offset < total) {
      const { data: contracts, error: fetchError } = await supabase
        .from('sam_contracts')
        .select(`
          notice_id,
          title,
          naics_code,
          agency,
          due_date,
          description,
          set_asides
        `)
        .range(offset, offset + CHUNK_SIZE - 1)
        .order('notice_id');

      if (fetchError) {
        console.error('Error fetching contracts for scoring:', fetchError);
        errors++;
        offset += CHUNK_SIZE;
        continue;
      }

      if (!contracts || contracts.length === 0) break;

      // Transform to Contract-like objects for scoring
      const contractsForScoring = contracts.map(c => ({
        id: c.notice_id,
        notice_id: c.notice_id,
        title: c.title || '',
        naicsCode: c.naics_code || '',
        agency: c.agency || '',
        dueDate: c.due_date,
        description: c.description || '',
        setAsides: c.set_asides || [],
        matchScore: 0,
        isFavorited: false,
        notifications: [],
        status: 'open' as const,
        fundingAmount: 0
      }));

      // Cache scores for this chunk
      await cacheContractScores(userId, contractsForScoring as Contract[], profile);

      processed += contracts.length;
      offset += CHUNK_SIZE;

      // Report progress
      if (onProgress) {
        onProgress(processed, total);
      }

      // Small delay to avoid overwhelming the database
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return { success: true, processed, errors };
  } catch (error) {
    console.error('Error in batch score calculation:', error);
    return { success: false, processed, errors: errors + 1 };
  }
}

/**
 * Check if user needs score recalculation.
 * Returns true if:
 * 1. User has no cached scores
 * 2. User's profile was updated after last score calculation
 */
export async function needsScoreRecalculation(userId: string): Promise<boolean> {
  try {
    // Get user's profile scoring_fields_updated_at
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('scoring_fields_updated_at, updated_at')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Error checking profile:', profileError);
      return true; // Assume needs recalculation on error
    }

    // Get latest score cache entry
    const { data: scoreData, error: scoreError } = await supabase
      .from('user_contract_scores')
      .select('updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1);

    if (scoreError) {
      // Table might not exist yet
      return false;
    }

    // If no scores cached, needs calculation
    if (!scoreData || scoreData.length === 0) {
      return true;
    }

    // Compare timestamps
    const profileUpdated = new Date(profileData.scoring_fields_updated_at || profileData.updated_at);
    const lastScoreCalc = new Date(scoreData[0].updated_at);

    return profileUpdated > lastScoreCalc;
  } catch (error) {
    console.error('Error checking if scores need recalculation:', error);
    return false; // Don't trigger recalculation on error
  }
}
